import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Stack, styled } from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { ME } from '~/graphql/queries';
import { ROUTES } from '~/shared/constants';

/**
 * ConsentRouteGuard protects routes that require FAMILY_TREE_PROFILE consent,
 * ensuring that all users (both tree owners and invited editors) have given
 * consent before proceeding to the profile form.
 */
export const ConsentRouteGuard = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(ME, {
    fetchPolicy: 'network-only',
  });

  const me = data?.me;

  const needsConsent = me && !me.hasFamilyTreeProfileConsent;

  useEffect(() => {
    if (loading) {
      return;
    }

    if (error || !me) {
      navigate(ROUTES.LOGIN);
      return;
    }

    if (needsConsent) {
      navigate(ROUTES.ONBOARDING.TREE_PROFILE_CONSENT);
    }
  }, [loading, error, me, needsConsent, navigate]);

  if (loading) {
    return <LoadingContainer direction="row" />;
  }

  if (error || !me || needsConsent) {
    return null;
  }

  return <Outlet />;
};

const LoadingContainer = styled(Stack)({
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.custom.cream,
});
